import type {
  TFaceCensorshipDetectedRegion,
  TFaceCensorshipPoint,
  TImageCoordinateFrameMetadata,
  TImageToolFaceBlurExportRegion,
} from "../../../../services/api-image-tools";
import { clampToRange } from "../../../../utils/censorshipGeometry";

export type TFaceRegionOrigin = "detected" | "manual";

export type TFaceBlurRegion = {
  id: string;
  points: TFaceCensorshipPoint[];
  origin: TFaceRegionOrigin;
  fallbackUsed: boolean;
};

export type TNormalizedBounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type TFaceRegionMappingResult =
  | { regions: TFaceBlurRegion[]; error: null }
  | { regions: []; error: string };

export const FACE_COORDINATE_MAPPING_ERROR =
  "Detected face regions could not be aligned because the region or processing-frame metadata is missing or invalid. Retry processing this photo before export.";

const MANUAL_REGION_SIZE = 0.28;
const MANUAL_REGION_OFFSET = 0.04;

const clampNormalized = (value: number) => clampToRange(value, 0, 1);

const isFinitePoint = (point: unknown): point is TFaceCensorshipPoint => {
  if (!point || typeof point !== "object") return false;
  const candidate = point as Partial<TFaceCensorshipPoint>;
  return Number.isFinite(candidate.x) && Number.isFinite(candidate.y);
};

export const sanitizeNormalizedPolygon = (
  points: readonly TFaceCensorshipPoint[],
): TFaceCensorshipPoint[] | null => {
  const sanitized = points.filter(isFinitePoint).map(({ x, y }) => ({
    x: clampNormalized(x),
    y: clampNormalized(y),
  }));
  return sanitized.length >= 3 ? sanitized : null;
};

export const normalizeDetectedFaceRegions = ({
  regions,
  processedImageWidth,
  processedImageHeight,
}: {
  regions: readonly TFaceCensorshipDetectedRegion[];
  processedImageWidth: number;
  processedImageHeight: number;
}): TFaceBlurRegion[] => {
  if (
    !Number.isFinite(processedImageWidth) ||
    !Number.isFinite(processedImageHeight) ||
    processedImageWidth <= 0 ||
    processedImageHeight <= 0
  ) {
    return [];
  }

  return regions.flatMap((region, index) => {
    if (
      region.coord_space !== "processed_image_px" ||
      region.shape_type !== "polygon" ||
      !Array.isArray(region.geometry?.points)
    ) {
      return [];
    }
    const points = sanitizeNormalizedPolygon(
      region.geometry.points.filter(isFinitePoint).map(({ x, y }) => ({
        x: x / processedImageWidth,
        y: y / processedImageHeight,
      })),
    );
    if (!points) return [];

    return [
      {
        id: region.id || `detected-face-${index + 1}`,
        points,
        origin: "detected" as const,
        fallbackUsed: Boolean(region.helpers?.fallback_used),
      },
    ];
  });
};

const isQuarterTurn = (value: unknown): value is 0 | 90 | 180 | 270 =>
  value === 0 || value === 90 || value === 180 || value === 270;

export const isValidImageCoordinateFrame = (
  frame: TImageCoordinateFrameMetadata | null | undefined,
): frame is TImageCoordinateFrameMetadata => {
  if (
    !frame ||
    frame.source_coord_space !== "source_image_px" ||
    frame.processed_coord_space !== "processed_image_px" ||
    !Number.isInteger(frame.source_width_px) ||
    !Number.isInteger(frame.source_height_px) ||
    !Number.isInteger(frame.processed_width_px) ||
    !Number.isInteger(frame.processed_height_px) ||
    frame.source_width_px <= 0 ||
    frame.source_height_px <= 0 ||
    frame.processed_width_px <= 0 ||
    frame.processed_height_px <= 0 ||
    !isQuarterTurn(frame.source_to_processed_rotation_degrees_ccw)
  ) {
    return false;
  }

  const sideways =
    frame.source_to_processed_rotation_degrees_ccw === 90 ||
    frame.source_to_processed_rotation_degrees_ccw === 270;
  return sideways
    ? frame.processed_width_px === frame.source_height_px &&
        frame.processed_height_px === frame.source_width_px
    : frame.processed_width_px === frame.source_width_px &&
        frame.processed_height_px === frame.source_height_px;
};

/** Map normalized EXIF-transposed source coordinates into PIL's CCW result. */
export const sourceToProcessedNormalizedPoint = (
  point: TFaceCensorshipPoint,
  rotationDegreesCcw: 0 | 90 | 180 | 270,
): TFaceCensorshipPoint => {
  switch (rotationDegreesCcw) {
    case 90:
      return { x: point.y, y: 1 - point.x };
    case 180:
      return { x: 1 - point.x, y: 1 - point.y };
    case 270:
      return { x: 1 - point.y, y: point.x };
    default:
      return { ...point };
  }
};

/** Map normalized detector coordinates back into the canonical source frame. */
export const processedToSourceNormalizedPoint = (
  point: TFaceCensorshipPoint,
  rotationDegreesCcw: 0 | 90 | 180 | 270,
): TFaceCensorshipPoint => {
  switch (rotationDegreesCcw) {
    case 90:
      return { x: 1 - point.y, y: point.x };
    case 180:
      return { x: 1 - point.x, y: 1 - point.y };
    case 270:
      return { x: point.y, y: 1 - point.x };
    default:
      return { ...point };
  }
};

/**
 * Convert approved detector pixels into canonical normalized source regions.
 * Manual edits, browser preview overlays, and export all use this source frame.
 */
export const mapDetectedFaceRegionsToSource = ({
  regions,
  coordinateFrame,
}: {
  regions: readonly TFaceCensorshipDetectedRegion[];
  coordinateFrame: TImageCoordinateFrameMetadata | null | undefined;
}): TFaceRegionMappingResult => {
  if (!regions.length) return { regions: [], error: null };
  if (!isValidImageCoordinateFrame(coordinateFrame)) {
    return { regions: [], error: FACE_COORDINATE_MAPPING_ERROR };
  }

  const normalized = normalizeDetectedFaceRegions({
    regions,
    processedImageWidth: coordinateFrame.processed_width_px,
    processedImageHeight: coordinateFrame.processed_height_px,
  });
  if (normalized.length !== regions.length) {
    return { regions: [], error: FACE_COORDINATE_MAPPING_ERROR };
  }
  return {
    regions: normalized.map((region) => ({
      ...region,
      points: region.points.map((point) =>
        processedToSourceNormalizedPoint(
          point,
          coordinateFrame.source_to_processed_rotation_degrees_ccw,
        ),
      ),
    })),
    error: null,
  };
};

const uniqueManualId = (regions: readonly TFaceBlurRegion[]) => {
  const existingIds = new Set(regions.map(({ id }) => id));
  let suffix = 1;
  while (existingIds.has(`manual-face-${suffix}`)) suffix += 1;
  return `manual-face-${suffix}`;
};

export const createManualFaceRegion = (
  regions: readonly TFaceBlurRegion[],
): TFaceBlurRegion => {
  const offset = Math.min(
    MANUAL_REGION_OFFSET *
      regions.filter(({ origin }) => origin === "manual").length,
    (1 - MANUAL_REGION_SIZE) / 2,
  );
  const x = (1 - MANUAL_REGION_SIZE) / 2 + offset;
  const y = (1 - MANUAL_REGION_SIZE) / 2 + offset;

  return {
    id: uniqueManualId(regions),
    origin: "manual",
    fallbackUsed: false,
    points: [
      { x, y },
      { x: x + MANUAL_REGION_SIZE, y },
      { x: x + MANUAL_REGION_SIZE, y: y + MANUAL_REGION_SIZE },
      { x, y: y + MANUAL_REGION_SIZE },
    ],
  };
};

export const removeFaceRegion = (
  regions: readonly TFaceBlurRegion[],
  regionId: string,
) => regions.filter(({ id }) => id !== regionId);

export const projectNormalizedPoint = (
  point: TFaceCensorshipPoint,
  width: number,
  height: number,
) => ({
  x: clampNormalized(point.x) * width,
  y: clampNormalized(point.y) * height,
});

export const getFaceRegionBounds = (
  region: Pick<TFaceBlurRegion, "points">,
): TNormalizedBounds => {
  const xs = region.points.map(({ x }) => x);
  const ys = region.points.map(({ y }) => y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return {
    x,
    y,
    width: Math.max(...xs) - x,
    height: Math.max(...ys) - y,
  };
};

export const fitFaceRegionToBounds = (
  region: TFaceBlurRegion,
  requestedBounds: TNormalizedBounds,
): TFaceBlurRegion => {
  const currentBounds = getFaceRegionBounds(region);
  const width = clampToRange(requestedBounds.width, 0, 1);
  const height = clampToRange(requestedBounds.height, 0, 1);
  const x = clampToRange(requestedBounds.x, 0, Math.max(0, 1 - width));
  const y = clampToRange(requestedBounds.y, 0, Math.max(0, 1 - height));
  const points = region.points.map((point) => ({
    x:
      currentBounds.width > 0
        ? x + ((point.x - currentBounds.x) / currentBounds.width) * width
        : x,
    y:
      currentBounds.height > 0
        ? y + ((point.y - currentBounds.y) / currentBounds.height) * height
        : y,
  }));

  return {
    ...region,
    points: sanitizeNormalizedPolygon(points) || region.points,
  };
};

export const faceRegionsToExport = (
  regions: readonly TFaceBlurRegion[],
): TImageToolFaceBlurExportRegion[] =>
  regions.flatMap((region) => {
    const points = sanitizeNormalizedPolygon(region.points);
    return points ? [{ points }] : [];
  });

export const getLocalPolygonPoints = (region: TFaceBlurRegion) => {
  const bounds = getFaceRegionBounds(region);
  if (bounds.width <= 0 || bounds.height <= 0) return "";
  return region.points
    .map(
      ({ x, y }) =>
        `${((x - bounds.x) / bounds.width) * 100},${
          ((y - bounds.y) / bounds.height) * 100
        }`,
    )
    .join(" ");
};
